import { memo, useEffect } from 'react';
import { Field, WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import {
  FieldWrapper,
  FullSpanSubRowTD,
  SubRowLabel,
  SubRowLabelTD,
  TableSubRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import InputField from 'dashboard/components/FormControls/InputField';
import { TableActionTD } from 'dashboard/layouts/TableLayout';
import { ExpirationThresholdConfig } from 'dashboard/models/verification-check-config/id-expiration-detection';
import { indexForOverride } from 'dashboard/models/verification-check-config/utils';
import { CountryCodeLower } from 'dashboard/models/verification-template-version';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import useToggle from 'lib/hooks/useToggle';

const TableSubRowActionTD = styled(TableActionTD)`
  border-bottom: none;
`;

const StyledInputField = styled(InputField)`
  width: 200px;
`;

interface OverrideExpirationRowProps {
  baseName: string;
  label: string;
  countryCode: CountryCodeLower;
  idClass: GovernmentIdClass;
  disabled: boolean;
}

/**
 * expiration thresholds override by country code, idclass, and subdivision
 * reference: https://www.notion.so/withpersona/expiration_thresholds-2bec896376644d85b698a9a3070231ba
 */
const OverrideExpirationRow = memo(
  ({
    baseName,
    label,
    countryCode,
    idClass,
    disabled,
    input,
  }: OverrideExpirationRowProps & WrappedFieldProps) => {
    const expirationConfigs = input.value as ExpirationThresholdConfig[];

    const overrideIndex = indexForOverride({
      attributesField: expirationConfigs,
      countryCode,
      idClass,
    });
    const name = `${baseName}.${overrideIndex}.expiration-threshold-days`;
    const [overrideAttribute, { toggle: toggleOverride, set: setOverrideState }] = useToggle(
      overrideIndex !== -1,
    );

    useEffect(() => {
      setOverrideState(overrideIndex !== -1);
    }, [overrideIndex, setOverrideState]);

    const handleToggle = (enabled: boolean) => {
      if (enabled) {
        const overrideValues: ExpirationThresholdConfig = {
          'country-code': countryCode.toUpperCase(),
          subdivision: 'default',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'id-class': idClass,
          'expiration-threshold-days': 0,
        };
        input.onChange(expirationConfigs.concat(overrideValues));
      } else {
        const updatedConfig = [
          ...expirationConfigs.slice(0, overrideIndex),
          ...expirationConfigs.slice(overrideIndex + 1),
        ];
        input.onChange(updatedConfig);
      }

      toggleOverride();
    };

    return (
      <>
        <TableSubRow>
          <SubRowLabelTD>
            <Paragraph>{label}</Paragraph>
          </SubRowLabelTD>
          <TableSubRowActionTD>
            <ToggleSlider
              id='test'
              key={`toggle-${name}`}
              labelOn='On'
              labelOff='Off'
              value={overrideAttribute}
              onChange={handleToggle}
              disabled={disabled}
              data-test='override-toggle-slider__expiration-threshold-days'
            />
          </TableSubRowActionTD>
        </TableSubRow>
        <TableSubRow>
          <FullSpanSubRowTD>
            <Spacer height={1} />
            {overrideAttribute && (
              <FieldWrapper>
                <Field
                  size='md'
                  id={name}
                  key={name}
                  name={name}
                  type='text'
                  component={StyledInputField}
                  label={<SubRowLabel>Expiration threshold days</SubRowLabel>}
                  data-test='override-table-row__expiration-threshold-days'
                  disabled={disabled}
                />
              </FieldWrapper>
            )}
          </FullSpanSubRowTD>
        </TableSubRow>
      </>
    );
  },
);

OverrideExpirationRow.displayName = 'OverrideExpirationRow';
export default OverrideExpirationRow;
